import { createFileRoute, Link } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog'
import { ArrowLeft, ChevronRight, Code } from 'lucide-react'
import { QueryGuard } from '@/components/query-guard'
import { MessageBubble } from '@/features/chat-onboarding/components/message-bubble'
import type { ChatMessage } from '@/features/chat-onboarding/types'
import { useAdminOnboardingByIdQuery } from '@/hooks/api'
import { formatFullDate } from '@/utils/format'

export const Route = createFileRoute('/_protected/admin/onboarding/$id')({
  component: AdminOnboardingDetailPage,
})

const noop = () => {}

function AdminOnboardingDetailPage() {
  const { id } = Route.useParams()
  const onboardingQuery = useAdminOnboardingByIdQuery(id)

  return (
    <QueryGuard query={onboardingQuery} errorMessage="Failed to load onboarding conversation">
      {(detail) => {
        const uiMessages = (detail.uiMessages ?? []) as ChatMessage[]

        return (
          <>
            <nav className="border-b border-border">
              <div className="flex flex-wrap items-center gap-2 py-3">
                <Link
                  to="/admin/onboarding"
                  className="flex items-center gap-1 text-sm font-medium text-[#f97316] hover:underline"
                >
                  <ArrowLeft className="size-4" />
                  Onboarding
                </Link>
                <ChevronRight className="size-4 text-muted-foreground" />
                <span className="text-sm font-medium">{detail.userName || detail.userEmail}</span>
                <Badge
                  variant={
                    detail.status === 'completed'
                      ? 'success'
                      : detail.status === 'abandoned'
                        ? 'secondary'
                        : 'warning'
                  }
                  size="sm"
                >
                  {detail.status === 'completed'
                    ? 'Completed'
                    : detail.status === 'abandoned'
                      ? 'Abandoned'
                      : 'In Progress'}
                </Badge>
              </div>
            </nav>

            <div className="grid grid-cols-3 gap-4 py-4">
              <div>
                <p className="text-xs text-muted-foreground">User</p>
                <Link
                  to="/admin/users/$id"
                  params={{ id: detail.userId }}
                  className="text-sm text-[#f97316] hover:underline"
                >
                  {detail.userName || detail.userEmail}
                </Link>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Role</p>
                <p className="text-sm">{detail.userType ?? 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="text-sm">{formatFullDate(detail.createdAt)}</p>
              </div>
            </div>

            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  Chat Transcript ({uiMessages.length} messages)
                </h3>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Code className="mr-1.5 size-3.5" />
                      Raw Data
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>Raw Message Data</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                      {detail.messages.map((msg) => (
                        <div key={msg.id}>
                          <div className="mb-1 flex items-center gap-2">
                            <Badge
                              variant={msg.role === 'ASSISTANT' ? 'secondary' : 'warning'}
                              size="sm"
                            >
                              {msg.role}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatFullDate(msg.createdAt)}
                            </span>
                          </div>
                          <pre className="overflow-auto rounded-lg bg-muted p-4 text-xs whitespace-pre-wrap">
                            {typeof msg.content === 'string'
                              ? msg.content
                              : JSON.stringify(msg.content, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4 rounded-xl bg-[#141414] p-6">
                {uiMessages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={{ ...msg, resolved: true }}
                    onChoice={noop}
                    onConfirm={noop}
                    onMultiSelect={noop}
                  />
                ))}
                {uiMessages.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground">
                    No messages in this conversation.
                  </p>
                )}
              </div>
            </div>
          </>
        )
      }}
    </QueryGuard>
  )
}
